import React from 'react';
import { Job } from '@core/generated/gql-types';
import DaySequenceItem from '@/components/daySequence/DaySequenceItem';
import UpdateTimesheetModal from './UpdateTimesheetModal';
import EditTimesheetModal from './EditTimesheetModal';
import CreateTimesheetModal from './CreateTimesheetModal';
import CheckoutTimesheetModal from './CheckoutTimesheetModal';
import DeleteTimesheetModal from './DeleteTimesheetModal';
import { useJobTimesheets } from '@/modules/jobs/hooks/useJobTimesheets';
import { usePortalAccess } from '@/hooks';
import { getTimesheetTimesForApprove } from '@core/util/timesheetPortalActions.util';
import Button from '@/components/Button';

interface JobTimesheetsProps {
  job: Job;
}

const JobTimesheets: React.FC<JobTimesheetsProps> = ({ job }) => {
  const { isClientPortal } = usePortalAccess();
  const {
    isLoading,
    sortedDaySequences,
    canCreateTimesheet,
    isUpdateTimesheetConfirmationOpen,
    isEditTimesheetOpen,
    isCreateTimesheetOpen,
    isCheckoutTimesheetOpen,
    isDeleteTimesheetOpen,
    setIsCreateTimesheetOpen,
    updateTimesheet,
    editAction,
    handleApproveTimesheet,
    handleResolveTimesheetDispute,
    handleProposeTimesheetChanges,
    handleCreateTimesheet,
    handleCheckoutTimesheet,
    handleDeleteTimesheet,
    openUpdateTimesheetConfirmation,
    openEditTimesheetModal,
    openCheckoutTimesheetModal,
    openDeleteTimesheetModal,
    onCloseUpdateTimesheetConfirmation,
    onCloseEditTimesheet,
    onCloseCreateTimesheet,
    onCloseCheckoutTimesheet,
    onCloseDeleteTimesheet,
  } = useJobTimesheets(job, isClientPortal);

  const approveTimes = updateTimesheet
    ? getTimesheetTimesForApprove(updateTimesheet)
    : null;

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold">Timesheets</h2>
      <div className="space-y-4">
        <UpdateTimesheetModal
          isOpen={isUpdateTimesheetConfirmationOpen}
          onClose={onCloseUpdateTimesheetConfirmation}
          updateTimesheet={updateTimesheet || null}
          finalClockInTime={approveTimes?.clockInTime || ''}
          finalClockOutTime={approveTimes?.clockOutTime || ''}
          finalBreakTime={approveTimes?.breaks || null}
          confirmationVariant="approve"
          onApproveTimesheet={handleApproveTimesheet}
        />

        <EditTimesheetModal
          isOpen={isEditTimesheetOpen}
          onClose={onCloseEditTimesheet}
          job={job}
          updateTimesheet={updateTimesheet || null}
          editAction={editAction}
          onResolveTimesheetDispute={handleResolveTimesheetDispute}
          onProposeTimesheetChanges={handleProposeTimesheetChanges}
        />

        <CheckoutTimesheetModal
          isOpen={isCheckoutTimesheetOpen}
          onClose={onCloseCheckoutTimesheet}
          job={job}
          daySequence={updateTimesheet}
          onCheckout={handleCheckoutTimesheet}
        />

        <DeleteTimesheetModal
          isOpen={isDeleteTimesheetOpen}
          onClose={onCloseDeleteTimesheet}
          daySequence={updateTimesheet}
          onConfirmDelete={handleDeleteTimesheet}
          isDeleting={isLoading}
        />

        <CreateTimesheetModal
          isOpen={isCreateTimesheetOpen}
          onClose={onCloseCreateTimesheet}
          job={job}
          handleCreateTimesheet={handleCreateTimesheet}
        />

        {isLoading ? (
          <div className="flex items-center justify-center">Loading...</div>
        ) : (
          <>
            {sortedDaySequences.length > 0 ? (
              <div className="space-y-4">
                {sortedDaySequences.map((daySequence) => (
                  <DaySequenceItem
                    key={daySequence.id}
                    daySequence={daySequence}
                    isClientPortal={isClientPortal}
                    openEditTimesheetModal={openEditTimesheetModal}
                    openUpdateTimesheetConfirmation={
                      openUpdateTimesheetConfirmation
                    }
                    openCheckoutTimesheetModal={openCheckoutTimesheetModal}
                    openDeleteTimesheetModal={openDeleteTimesheetModal}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-start gap-2">
                <p className="text-sm text-gray-500">No recent activity</p>
              </div>
            )}
            {canCreateTimesheet && (
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsCreateTimesheetOpen(true)}
                className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
              >
                Create timesheet
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JobTimesheets;
